import {
  getTicketDetailFromStorage,
  setTicketDetailInStorage,
  TicketDetail,
} from "./ticket";

export type MakeAWishStatusUpdate = {
  status: "in_use";
  ticketDetail: string;
  wish: string;
  gender: string;
  join: string;
  friendGender: string | null;
};

const formatLastNameAsInitial = (lastName?: string | null) => {
  if (!lastName) return lastName;

  const trimmed = lastName.trim();
  if (!trimmed) return lastName;

  const parts = trimmed.split(/\s+/);
  const last = parts[parts.length - 1];
  if (!last) return lastName;

  return `${last.charAt(0).toUpperCase()}.`;
};

/**
 * Builds the `updateMakeAWishStatus` payload from what the flow saved in
 * localStorage (ticket, wish, join, gender, friendGender). Returns null when a
 * required piece is missing. Also stores the display-formatted ticket
 * (last name as initial, user_name) — same behaviour as the old /magic screen.
 */
export const buildMakeAWishStatusUpdate = (): MakeAWishStatusUpdate | null => {
  const ticketDetail = getTicketDetailFromStorage();
  const wish = localStorage.getItem("wish");
  const gender = localStorage.getItem("gender");
  const friendGender = localStorage.getItem("friendGender");
  const join = localStorage.getItem("join");

  if (!ticketDetail || !wish || !gender || !join) return null;

  const parsedTicketDetail: TicketDetail = { ...ticketDetail };

  if (
    !parsedTicketDetail.first_name &&
    !parsedTicketDetail.last_name &&
    parsedTicketDetail.user &&
    typeof parsedTicketDetail.user === "object" &&
    "last_name" in parsedTicketDetail.user
  ) {
    parsedTicketDetail.last_name = (
      parsedTicketDetail.user as { last_name?: string }
    ).last_name;
  }

  if (parsedTicketDetail.last_name) {
    parsedTicketDetail.last_name =
      formatLastNameAsInitial(parsedTicketDetail.last_name as string) ?? "";
  }

  if (
    parsedTicketDetail.user &&
    typeof parsedTicketDetail.user === "object" &&
    "last_name" in parsedTicketDetail.user
  ) {
    const user = parsedTicketDetail.user as { last_name?: string };
    if (user.last_name) {
      user.last_name = formatLastNameAsInitial(user.last_name) ?? "";
    }
  }

  const outerFirstName =
    (parsedTicketDetail.first_name as string) ||
    (parsedTicketDetail.user as { first_name?: string })?.first_name ||
    "";
  const outerLastName = (parsedTicketDetail.last_name as string) || "";

  if (outerFirstName || outerLastName) {
    parsedTicketDetail.user_name = `${outerFirstName}${outerFirstName && outerLastName ? " " : ""}${outerLastName}`;
  }

  setTicketDetailInStorage(parsedTicketDetail);

  return {
    status: "in_use",
    ticketDetail: JSON.stringify(parsedTicketDetail),
    wish,
    gender,
    join,
    friendGender: friendGender || null,
  };
};
